/**
 * Brand-query attribution.
 *
 * Classifies the visit as brand / non-brand / direct traffic from the paid
 * keyword (utm_term, Google Ads {keyword}), the campaign name and the search
 * referrer. The result is attached to every GTM/GA4 event and to the CRM lead
 * payload, so brand-query conversions can be reconciled between Google Ads,
 * GA4 and the CRM without extra instrumentation.
 */
import { getStoredUTMParams } from '@/hooks/useUTMTracking';
import { canMeasure } from '@/lib/trackingPrivacy';

export type QueryType = 'brand' | 'non_brand' | 'unknown';
export type ChannelGroup =
  | 'paid_search'
  | 'paid_social'
  | 'organic_search'
  | 'social'
  | 'referral'
  | 'direct';

/** Terms that identify a branded search for Hair Art Clinics. */
export const BRAND_TERMS: readonly string[] = [
  'hair art',
  'hairart',
  'hair art clinic',
  'hairartclinics',
  'hairartclinics.net',
  'hair art clinics den haag',
  'siyabent',
  'alagoz',
  'alagöz',
];

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[+_-]/g, ' ').replace(/\s+/g, ' ').trim();

export const isBrandTerm = (term: string | null | undefined): boolean => {
  if (!term) return false;
  const t = normalize(term);
  if (!t) return false;
  return BRAND_TERMS.some((brand) => t.includes(brand));
};

export interface QueryAttribution {
  query_type: QueryType;
  channel_group: ChannelGroup;
  /** Keyword/term when the ad platform passes one through. */
  search_term: string;
  /** Brand term that matched, useful for Ads/GA4 segment names. */
  matched_brand_term: string;
  campaign: string;
  source: string;
  medium: string;
  gclid: string;
}

const resolveChannel = (params: ReturnType<typeof getStoredUTMParams>): ChannelGroup => {
  if (params.gclid || params.gbraid || params.wbraid || params.msclkid) return 'paid_search';
  if (params.fbclid || params.ttclid) return 'paid_social';
  const medium = (params.utm_medium || '').toLowerCase();
  if (medium.includes('cpc') || medium.includes('paid') || medium.includes('ppc')) return 'paid_search';
  if (medium.includes('social')) return 'paid_social';
  const referrer = params.referrer || '';
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (/google|bing|duckduckgo|yahoo|ecosia|yandex/.test(host)) return 'organic_search';
    if (/facebook|instagram|linkedin|tiktok|youtube|x\.com|twitter/.test(host)) return 'social';
    return 'referral';
  } catch {
    return 'referral';
  }
};

/** Reads stored UTM/click-id data and derives the brand-query attribution. */
export const getQueryAttribution = (): QueryAttribution => {
  if (typeof window === 'undefined') {
    return {
      query_type: 'unknown',
      channel_group: 'direct',
      search_term: '',
      matched_brand_term: '',
      campaign: '',
      source: '',
      medium: '',
      gclid: '',
    };
  }

  const params = getStoredUTMParams();
  const term = params.utm_term || '';
  const campaign = params.utm_campaign || '';
  const channel_group = resolveChannel(params);

  const candidates = [term, campaign, params.utm_content || ''];
  const matched = BRAND_TERMS.find((brand) => candidates.some((c) => normalize(c).includes(brand))) || '';

  let query_type: QueryType = 'unknown';
  if (matched) {
    query_type = 'brand';
  } else if (term || campaign) {
    query_type = 'non_brand';
  }

  return {
    query_type,
    channel_group,
    search_term: term,
    matched_brand_term: matched,
    campaign,
    source: params.utm_source || '',
    medium: params.utm_medium || '',
    gclid: params.gclid || params.gbraid || params.wbraid || '',
  };
};

/** GA4 client id from the _ga cookie, so CRM conversions join the web session. */
export const getGaClientId = (): string => {
  if (typeof document === 'undefined' || !canMeasure('analytics')) return '';
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match ? match[1] : '';
};

/** Attribution payload stored on the lead record by track_lead_activity. */
export const getLeadAttributionPayload = (): Record<string, string> => {
  const a = getQueryAttribution();
  return {
    gclid: a.gclid,
    utm_term: a.search_term,
    utm_content: getStoredUTMParams().utm_content || '',
    utm_source: a.source,
    utm_medium: a.medium,
    utm_campaign: a.campaign,
    query_type: a.query_type,
    channel_group: a.channel_group,
    matched_brand_term: a.matched_brand_term,
    ga_client_id: getGaClientId(),
  };
};
